/**
 * ConversationSidebar - Displays conversation history for an arche
 *
 * Shows a list of past conversations that can be loaded, with options to
 * create new conversations, archive, delete, and export.
 */

import { useState } from 'react';
import {
  MessageSquare,
  Plus,
  Trash2,
  Archive,
  Download,
  Share2,
  MoreHorizontal,
  Pencil,
  Check,
  X,
  History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ConversationResponse } from '@/lib/api';

interface ConversationSidebarProps {
  /** List of conversations to display */
  conversations: ConversationResponse[];
  /** Currently active conversation ID */
  activeConversationId: string | null;
  /** Whether conversations are loading */
  loading: boolean;
  /** Callback when user creates a new conversation */
  onNewConversation: () => void;
  /** Callback when user loads a conversation */
  onLoadConversation: (conversationId: string) => void;
  /** Callback when user updates conversation title */
  onUpdateTitle: (conversationId: string, title: string) => void;
  /** Callback when user archives a conversation */
  onArchiveConversation: (conversationId: string) => void;
  /** Callback when user deletes a conversation */
  onDeleteConversation: (conversationId: string) => void;
  /** Callback when user exports a conversation */
  onExportConversation: (conversationId: string, format: 'json' | 'markdown') => void;
  /** Callback when user shares a conversation */
  onShareConversation: (conversationId: string) => void;
  /** Whether streaming is in progress (disables some actions) */
  isStreaming?: boolean;
  /** Optional trigger button to use instead of default */
  trigger?: React.ReactNode;
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

interface ConversationItemProps {
  conversation: ConversationResponse;
  isActive: boolean;
  isStreaming?: boolean;
  onLoad: () => void;
  onUpdateTitle: (title: string) => void;
  onArchive: () => void;
  onDelete: () => void;
  onExport: (format: 'json' | 'markdown') => void;
  onShare: () => void;
}

function ConversationItem({
  conversation,
  isActive,
  isStreaming,
  onLoad,
  onUpdateTitle,
  onArchive,
  onDelete,
  onExport,
  onShare,
}: ConversationItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conversation.title || '');

  const handleSaveTitle = () => {
    if (editTitle.trim()) {
      onUpdateTitle(editTitle.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditTitle(conversation.title || '');
    setIsEditing(false);
  };

  const displayTitle = conversation.title || 'Untitled conversation';

  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors',
        isActive
          ? 'bg-primary/10 border border-primary/20'
          : 'hover:bg-muted/50'
      )}
      onClick={() => !isEditing && onLoad()}
    >
      <MessageSquare className="h-4 w-4 flex-shrink-0 text-muted-foreground" />

      {isEditing ? (
        <div className="flex-1 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveTitle();
              if (e.key === 'Escape') handleCancelEdit();
            }}
            className="h-7 text-sm"
            autoFocus
          />
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleSaveTitle}>
            <Check className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCancelEdit}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{displayTitle}</p>
            <p className="text-xs text-muted-foreground">
              {conversation.messageCount} message{conversation.messageCount !== 1 ? 's' : ''} · {formatRelativeDate(conversation.updatedAt)}
            </p>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                disabled={isStreaming}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => setIsEditing(true)}>
                <Pencil className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onShare}>
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExport('markdown')}>
                <Download className="h-4 w-4 mr-2" />
                Export as Markdown
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onArchive}>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="text-red-500 focus:text-red-500 focus:bg-red-500/10">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );
}

export function ConversationSidebar({
  conversations,
  activeConversationId,
  loading,
  onNewConversation,
  onLoadConversation,
  onUpdateTitle,
  onArchiveConversation,
  onDeleteConversation,
  onExportConversation,
  onShareConversation,
  isStreaming = false,
  trigger,
}: ConversationSidebarProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleLoadConversation = (conversationId: string) => {
    onLoadConversation(conversationId);
    setIsOpen(false);
  };

  const handleNewConversation = () => {
    onNewConversation();
    setIsOpen(false);
  };

  const handleExport = async (conversationId: string, format: 'json' | 'markdown') => {
    onExportConversation(conversationId, format);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-1.5" title="History">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">History</span>
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="left" className="w-[320px] p-0">
        <SheetHeader className="px-4 py-3 border-b">
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Conversation History
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-60px)]">
          {/* New conversation button */}
          <div className="p-3 border-b">
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={handleNewConversation}
              disabled={isStreaming}
            >
              <Plus className="h-4 w-4" />
              New Conversation
            </Button>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto p-2">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="px-3 py-2">
                    <Skeleton className="h-4 w-3/4 mb-1" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">No conversations yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Start a new conversation to see it here
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {conversations.map((conv) => (
                  <ConversationItem
                    key={conv.conversationId}
                    conversation={conv}
                    isActive={conv.conversationId === activeConversationId}
                    isStreaming={isStreaming}
                    onLoad={() => handleLoadConversation(conv.conversationId)}
                    onUpdateTitle={(title) => onUpdateTitle(conv.conversationId, title)}
                    onArchive={() => onArchiveConversation(conv.conversationId)}
                    onDelete={() => onDeleteConversation(conv.conversationId)}
                    onExport={(format) => handleExport(conv.conversationId, format)}
                    onShare={() => onShareConversation(conv.conversationId)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ConversationSidebar;
